import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  first,
  map,
  Observable,
  shareReplay,
  Subject,
  tap,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { Contact } from 'src/app/core/models/contact/contact.interface';
import { ContactListItemView } from 'src/app/core/models/contact/contact-list-item-view.interface';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { RequestModel } from 'src/app/core/models/request-model.interface';
import { SortChangeEvent } from 'src/app/core/directives/sortable-header.directive';
import { ContactService } from 'src/app/core/services/contact.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeProfileComponent } from '../../employees/employee-profile/employee-profile.component';

@Component({
  selector: 'app-contacts',
  templateUrl: './contacts.component.html',
  styleUrl: './contacts.component.scss',
  standalone: false,
})
export class ContactsComponent {
  private readonly modalService = inject(NgbModal);
  private readonly contactService = inject(ContactService);
  private readonly permissionService = inject(PermissionService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  public readonly currentLang: string;

  public readonly emptyContactListParam: Record<string, string> = {
    uk: 'контактів',
    en: 'contact',
  };

  public readonly canCreateContact$ = this.permissionService.userPermissions$.pipe(
    map(permissions =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Contact,
        UserCompanyPermissions.Create,
      ),
    ),
    shareReplay(),
  );

  public readonly canViewOrganizations$ = this.permissionService.userPermissions$.pipe(
    map(permissions =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Organization,
        UserCompanyPermissions.View,
      ),
    ),
    shareReplay(),
  );

  public companyId = '';
  public isLoading = false;
  public isCreateContactModalOpen = false;
  public contacts$: Observable<PagedList<ContactListItemView>> = EMPTY;
  public searchTerm = '';

  private readonly searchChanges$ = new Subject<string>();
  private request: RequestModel = {
    page: 1,
    pageSize: 10,
    filters: [],
    sortColumn: 'fullName',
    sortOrder: 'asc',
  };

  constructor() {
    const route = inject(ActivatedRoute);

    route.parent?.parent?.params
      .pipe(
        first(),
        tap(params => {
          this.isLoading = true;
          this.companyId = params['id'];
          this.contactService.setContactsUrl(this.companyId);
        }),
        tap(() => this.loadContacts()),
      )
      .subscribe();

    this.currentLang = this.translateService.currentLang ?? Constants.DefaultLanguage;

    this.searchChanges$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(value => {
          this.request.page = 1;
          this.request.searchTerm = value.trim() || undefined;
          this.isLoading = true;
          this.loadContacts();
        }),
      )
      .subscribe();
  }

  public openCreateContactModal(): void {
    this.isCreateContactModalOpen = true;
  }

  public closeCreateContactModal(): void {
    this.isCreateContactModalOpen = false;
  }

  public openImport(): void {
    void this.router.navigate(['/companies', this.companyId, 'settings'], {
      queryParams: {
        section: 'imports',
        importEntity: 'contacts',
      },
    });
  }

  public onContactCreated(): void {
    this.closeCreateContactModal();
    this.isLoading = true;
    this.loadContacts();
  }

  public onPageChange(page: number): void {
    this.request.page = page;
    this.loadContacts();
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;
    this.request.page = 1;
    this.loadContacts();
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChanges$.next(value);
  }

  public onSortChange(event: SortChangeEvent): void {
    this.request.sortColumn = event.column;
    this.request.sortOrder = event.order;
    this.request.page = 1;
    this.isLoading = true;
    this.loadContacts();
  }

  public get activeSortColumn(): string {
    return this.request.sortColumn ?? '';
  }

  public get activeSortOrder(): 'asc' | 'desc' {
    return this.request.sortOrder ?? 'asc';
  }

  public viewProfile(employeeId: string): void {
    const modalRef = this.modalService.open(EmployeeProfileComponent, {
      size: 'lg',
      windowClass: 'br-50',
    });

    modalRef.componentInstance.id = employeeId;
    modalRef.componentInstance.showDeleteButton = false;
  }

  private loadContacts(): void {
    this.contacts$ = this.contactService.getCompanyContactsPaged(this.request).pipe(
      map(page => ({
        ...page,
        items: page.items.map(contact => this.mapContactToListItemView(contact)),
      })),
      tap(() => (this.isLoading = false)),
      catchError(error => {
        this.isLoading = false;
        throw error;
      }),
      shareReplay(1),
    );
  }

  private mapContactToListItemView(contact: Contact): ContactListItemView {
    const entityType = this.getEntityType(contact);

    return {
      id: contact.id,
      fullName: contact.fullName,
      number: contact.number ?? '',
      displayNumber: this.getDisplayNumber(contact),
      birthDate: contact.birthDate ?? null,
      email: contact.email ?? '',
      phoneNumber: contact.phoneNumber ?? '',
      organizationTitle: contact.organizationTitle ?? '',
      organizationId: contact.organizationId ?? '',
      hasOrganization: Boolean(contact.organizationId),
      responsibleUserId: contact.responsibleUserId,
      responsibleUserName: contact.responsibleUserName ?? '',
      hasResponsibleUser: Boolean(contact.responsibleUserId),
      entityType,
      entityTypeTitle: this.translateService.instant(
        this.getEntityTypeTitleKey(entityType),
      ),
    };
  }

  private getEntityType(contact: Contact): 'regular' | 'customer' | 'vendor' {
    if (contact.isCustomer) {
      return 'customer';
    }

    if (contact.isVendor) {
      return 'vendor';
    }

    return 'regular';
  }

  private getDisplayNumber(contact: Contact): string {
    if (contact.businessCentralId) {
      return contact.number || contact.businessCentralId;
    }

    return contact.number || contact.id;
  }

  private getEntityTypeTitleKey(
    entityType: 'regular' | 'customer' | 'vendor',
  ): string {
    if (entityType === 'customer') {
      return 'TOOLTIPS.ENTITY_TYPE_CUSTOMER';
    }

    if (entityType === 'vendor') {
      return 'TOOLTIPS.ENTITY_TYPE_VENDOR';
    }

    return 'TOOLTIPS.ENTITY_TYPE_REGULAR';
  }
}
